/**
 * Shared hook backing both the /app/welcome checklist and the persistent
 * OnboardingNudge. Resolves the per-org setup state (code source, usable
 * agent credential, first session, document branding, team) and returns the
 * checklist items.
 *
 * The credential test mirrors the session-create gate exactly (an active
 * personal link on any provider, OR a deployment-wide central/server billing
 * key, OR an organisation custom model that can run now; see
 * ``useCanRunSessions``) so the checklist's "credential" row can't disagree
 * with whether a turn will actually start. For a viewer who isn't a member of the org
 * (e.g. a superadmin), the test switches to the org-wide fact from
 * ``GET /orgs/{slug}/setup-state`` — the viewer's own linked accounts are
 * irrelevant to whether that org's members can run turns.
 */
import {
  useAuthMatchRules,
  useBrandProfiles,
  useCodeSources,
  useMe,
  useOrgLedger,
  useOrgMembers,
  useOrgSetupFacts,
  useSessions,
} from "@/api/hooks";
import { useOrgBilling } from "@/api/billing";
import { administersOrg, canOpenBilling } from "@/features/admin/billingAccess";

import { useFlowState } from "./onboardingFlowState";
import { buildSetupChecklist, countIncomplete, type ChecklistItem } from "./setupChecklist";
import { useCanRunSessions } from "./useCanRunSessions";

export type SetupChecklist = {
  /** The active org slug the checklist is scoped to (null when none). */
  slug: string | null;
  items: ChecklistItem[];
  /** Tracked items (``done`` defined) still incomplete, less a code
   *  source the viewer skipped in the two-step start. */
  incompleteCount: number;
  /** True once the queries backing the tracked items have settled — guards
   *  against a false "incomplete" flash before data loads. */
  ready: boolean;
};

export function useSetupChecklist(slugArg?: string | null): SetupChecklist {
  const me = useMe();
  const slug = slugArg ?? me.data?.admin_scope?.active_org_slug ?? null;
  // useCodeSources gates on Boolean(slug) — an empty string disables the
  // query (no slug → nothing to check).
  const sources = useCodeSources(slug ?? "");
  // Whether the viewer can run sessions: their own usable login on a
  // registered runtime (``useProviderCredentials``), or an organisation
  // custom model that runs through the gateway (``useCanRunSessions``).
  const credentials = useCanRunSessions(slug);
  // "First session" is the ORGANISATION's step: a new member of an org
  // that has been working for months was told a first session was still
  // missing, because their own (and shared) sessions were all we read.
  // The org's answer wins; the viewer's own list only stands in while it
  // loads or if it fails, so the step doesn't flicker.
  const orgSetup = useOrgSetupFacts(slug ?? "");
  const sessions = useSessions();
  // The prompt only ever shows to someone who may open admin, so gate the
  // brand query on ``can_access_admin`` (an empty slug disables it) rather
  // than send it for a plain member on every page.
  const canManage = Boolean(me.data?.admin_scope?.can_access_admin);
  const orgGate = canManage ? (slug ?? "") : "";
  // Every profile's asset count, not the primary's assets alone: a
  // template imported into a new profile brings a logo too.
  const brandProfiles = useBrandProfiles(orgGate);
  // Members and sign-in rules are organisation-ADMIN requests: a manager
  // got two 403s on every page, and a team step that could never be done.
  const canInviteTeam = administersOrg(me.data, slug);
  const teamGate = canInviteTeam ? (slug ?? "") : "";
  const members = useOrgMembers(teamGate);
  const authRules = useAuthMatchRules(teamGate);
  // Billing is an organisation-admin surface AND inert until an operator
  // opts in, so gate both queries on the flag as well as the role — an
  // empty slug disables them, which keeps a manager, a plain member (and
  // every billing-off deployment) from 403/404-spamming two endpoints on
  // every page load.
  const billingEnabled = Boolean(me.data?.features?.billing_enabled);
  const opensBilling = canOpenBilling(me.data, slug);
  const billingGate = opensBilling ? (slug ?? "") : "";
  const billing = useOrgBilling(billingGate);
  const ledger = useOrgLedger(billingGate);

  // A viewer who isn't a member of the org (e.g. a superadmin inspecting
  // another org) must see the ORG's credential fact, not their own
  // linked accounts — a superadmin with no personal login otherwise saw
  // "1 step left" on an org that had been running turns for weeks.
  // Members keep the live per-provider check so a brand-new link shows
  // up immediately rather than waiting on the org-facts query.
  const isMember = (me.data?.admin_scope?.org_member_slugs ?? []).includes(
    slug ?? "",
  );
  const viewerHasCredential = credentials.any;
  const hasCredential = isMember
    ? viewerHasCredential
    : (orgSetup.data?.member_has_agent_login ?? viewerHasCredential);

  const items = buildSetupChecklist({
    hasSource: (sources.data?.length ?? 0) > 0,
    hasCredential,
    hasSession:
      orgSetup.data?.has_sessions ?? (sessions.data?.length ?? 0) > 0,
    // Branding is "done" once some profile carries the org's own look
    // (``brand_status``: an asset, or a look token off the defaults — the
    // palette every new org is seeded with doesn't count). Rows from an
    // older backend fall back to "has an asset".
    hasBrand: (brandProfiles.data ?? []).some((p) =>
      p.brand_status ? p.brand_status === "custom" : p.asset_count > 0,
    ),
    // Team is "done" once there's a 2nd member OR a verified sign-in rule
    // (a pending claim admits no one yet).
    hasTeam:
      (members.data?.length ?? 0) > 1 ||
      (authRules.data ?? []).some((r) => (r.status ?? "verified") === "verified"),
    canInviteTeam,
    billingEnabled,
    canOpenBilling: opensBilling,
    // Either alone is enough. A plan is not mandatory — the credit gate
    // blocks on BALANCE and never on having an agreement — so an org
    // running on purchased credits is covered, and one on a plan is
    // covered before its first top-up.
    hasBillingCover:
      (ledger.data?.balance_micros ?? 0) > 0 ||
      billing.data?.agreement != null,
  });
  const flowState = useFlowState(slug);
  const incompleteCount = countIncomplete(items, {
    sourceSkipped: flowState.skipped.includes("source"),
  });
  // ``ready`` must wait for EVERY query backing a tracked item, or the nudge
  // renders with a wrong/flickering count before data settles. The
  // ``credential`` row depends on all the credential/billing queries — omitting
  // them (the original bug) let the nudge flash an incorrect "N steps left" the
  // moment me/sources/sessions loaded, so it read as unreliable / not-there.
  const ready =
    !me.isLoading &&
    !sources.isLoading &&
    !sessions.isLoading &&
    // Waits for the org's answer too, or a new member of a working org
    // would see "1 step left" flash until it lands.
    !orgSetup.isLoading &&
    credentials.ready &&
    // Disabled (non-manager) queries report ``isLoading:false``, so this
    // never blocks a plain member.
    !brandProfiles.isLoading &&
    !members.isLoading &&
    !authRules.isLoading &&
    // Disabled queries report isLoading:false, so this is a no-op when
    // billing is off or the viewer is not a manager.
    !billing.isLoading &&
    !ledger.isLoading;

  return { slug, items, incompleteCount, ready };
}
